import React, { useContext, useEffect, useState } from "react";
import { FloatingLabel, Modal, Form, Button } from "react-bootstrap";
import Utils from "../../../Utils";
import ContextComponent from "../../../AppContext";
import { useNavigate, useParams } from "react-router-dom";

const FolderActionsModal = ({
  modalType,
  modalCanOpen,
  setModalCanOpen,
  item,
}) => {
  let [folderName, setFolderName] = useState("");
  let {
    folderStructure,
    setFolderStructure,
    setCanRedirectToLogin,
    setAlert,
    setVirtualFolderData,
  } = useContext(ContextComponent);
  let projectId = item?.id;
  let parentId = item?.parentId;
  let navigate = useNavigate();
  const activeNavId = useParams();

  const handleFolderName = (e) => {
    if (e.target.value !== " ") {
      setFolderName(e.target.value);
    } else {
      setAlert({
        show: true,
        message: "Project name should not be empty.",
      });
    }
  };

  const findParentId = (tree, targetId, parentId = null) => {
    for (const node of tree) {
      if (node.id === targetId) {
        return parentId;
      }

      if (node.children?.length) {
        const result = findParentId(node.children, targetId, node.id);
        if (result !== null) return result;
      }
    }

    return null;
  };

  const loadAllFolders = () => {
    const folderSuccess = (res) => {
      const allFolders = res.data;
      const virtualFolder = [
        {
          id: Utils.VIRTUAL_FOLDER_NAME,
          name: "Root",
          path: "/Root",
          children: allFolders,
        },
      ];
      const buildTreeData = (items) => {
        return items.map((item) => ({
          id: item.id,
          name: item.name,
          path: item.path,
          parentId: findParentId(allFolders, item.id),
          children: item.children?.length ? buildTreeData(item.children) : [],
        }));
      };
      const treeData = buildTreeData(allFolders);
      const virtualData = buildTreeData(virtualFolder);
      setVirtualFolderData(virtualData);
      setFolderStructure(treeData);
    };

    const folderFail = (err) => {
      err?.message?.length && console.log(err);
      if (err?.redirect) {
        setCanRedirectToLogin(true);
      }
    };
    Utils.getAllFolders().then(folderSuccess, folderFail);
  };

  const success = (res) => {
    if (modalType === "add") {
      setAlert({
        show: true,
        message: "Project added successfully.",
      });
    } else {
      setAlert({
        show: true,
        message: "Project updated successfully.",
      });
    }
    if (res.status === 200) {
      loadAllFolders();
      setModalCanOpen(false);
      setFolderName("");
    }
  };

  const fail = (err) => {
    if (err?.redirect) {
      setCanRedirectToLogin(true);
    }
    setAlert({
      show: true,
      message:
        err?.error?.response?.data?.message ||
        "Something went wrong. Please try again.",
      type: "danger",
    });
    setModalCanOpen(false);
    setFolderName("");
    err?.message?.length && console.log(err);
  };

  const deleteSuccess = (res, projectId) => {
    const DEFAULT_NAV_ID = Utils.VIRTUAL_FOLDER_NAME;
    const actId = activeNavId["*"]?.split("/")?.pop();
    let parentIdForNavigation = DEFAULT_NAV_ID;

    const isFolderView = [
      "sites",
      "users",
      "settings",
      "site-reports",
    ].includes(actId);
    const isProjectDeleted = actId === projectId;

    if (isProjectDeleted || isFolderView) {
      const parentId = findParentId(folderStructure, projectId);
      parentIdForNavigation = parentId ?? DEFAULT_NAV_ID;
    } else {
      parentIdForNavigation = actId ?? DEFAULT_NAV_ID;
    }
    setAlert({
      show: true,
      message: res?.data?.message || "Folder deleted successfully.",
      type: "success",
    });
    if (res.status === 200) {
      loadAllFolders();
      setModalCanOpen(false);
      navigate(Utils.APP_URLS.PROJECTS_PAGE + "/" + parentIdForNavigation);
    }
  };

  const deleteFail = (err) => {
    if (err?.redirect) {
      setCanRedirectToLogin(true);
    }
    setAlert({
      show: true,
      message:
        err?.error?.response?.data?.message ||
        "Something went wrong. Please try again.",
      type: "danger",
    });
    setModalCanOpen(false);
    err?.message?.length && console.log(err);
  };

  const handleSave = () => {
    const actions = {
      add: () => Utils.addFolder(projectId, folderName).then(success, fail),
      edit: () =>
        Utils.editFolder(projectId, parentId, folderName).then(success, fail),
      delete: () =>
        Utils.deleteFolder(projectId).then(
          (res) => deleteSuccess(res, projectId),
          deleteFail
        ),
    };

    if (modalType && actions[modalType]) {
      actions[modalType]();
    } else {
      console.warn("Unknown modal type:", modalType);
    }
  };

  useEffect(() => {
    setModalCanOpen(true);
    if (modalType === "edit") {
      setFolderName(item?.name);
    } else if (modalType === "add" || modalType === "delete") {
      setFolderName("");
    }
  }, [modalType, setModalCanOpen, item?.name]);

  const handleClose = () => {
    setModalCanOpen(false);
    if (modalType === "add") {
      setFolderName("");
    }
  };

  return (
    <>
      <div>
        <Modal centered show={modalCanOpen} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalType === "add" && `Add Project (${item?.name})`}
              {modalType === "edit" && "Edit Project"}
              {modalType === "delete" && "Delete Project"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(modalType === "add" || "edit") && modalType !== "delete" && (
              <FloatingLabel label="Project Name">
                <Form.Control
                  type="text"
                  className="indi-input-field"
                  id="folderName"
                  placeholder="Enter project name"
                  autoComplete="off"
                  onChange={handleFolderName}
                  value={folderName}
                />
              </FloatingLabel>
            )}
            {modalType === "delete" && (
              <div className="indi-folder-action-modal-body-delete-content">
                Are you sure to delete{" "}
                <span className="indi-folder-action-modal-body-delete-content-folder-item">
                  {item?.name}
                </span>{" "}
                project?
              </div>
            )}
            <div className="indi-add-footer">
              <div className="indi-add-page-footer-btn-wrapper float-right">
                <Button variant="primary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={
                    (modalType === "add" || modalType === "edit") &&
                    folderName.trim().length === 0
                  }
                >
                  {modalType === "add" && "Add"}
                  {modalType === "edit" && "Edit"}
                  {modalType === "delete" && "Delete"}
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default FolderActionsModal;
